import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Alert, AlertDescription } from '../ui/alert';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import { 
  Save, 
  Loader2, 
  Mail, 
  Phone, 
  MapPin, 
  Globe, 
  Clock,
  FileText,
  Shield,
  Cookie,
  Scale,
  CheckCircle,
  AlertCircle,
  Home,
  Type,
  BarChart3,
  MousePointer,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link as LinkIcon,
  Heading1,
  Heading2,
  Heading3,
  Undo,
  Redo,
  ImageIcon,
  Upload,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Image Upload Component
const ImageUploader = ({ currentImage, onImageChange, label, folder = 'site' }) => {
  const { token } = useAuth();
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona un archivo de imagen');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('La imagen no puede superar los 5MB');
      return;
    }

    setUploading(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result;
        
        // Upload to server
        const response = await fetch(`${API_URL}/api/upload-image`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            image: base64,
            filename: file.name,
            folder: folder
          })
        });

        if (response.ok) {
          const data = await response.json();
          onImageChange(`${API_URL}${data.url}`);
        } else {
          alert('Error al subir la imagen');
        }
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Error uploading image:', err);
      alert('Error al subir la imagen');
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex gap-4 items-start">
        {/* Preview */}
        <div className="w-40 h-24 border rounded-lg overflow-hidden bg-muted flex items-center justify-center">
          {currentImage ? (
            <img src={currentImage} alt="Preview" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon className="h-8 w-8 text-muted-foreground" />
          )}
        </div>
        
        {/* Upload buttons */}
        <div className="flex flex-col gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*"
            className="hidden"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            {currentImage ? 'Cambiar imagen' : 'Subir imagen'}
          </Button>
          {currentImage && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onImageChange('')}
              className="text-destructive"
            >
              <X className="h-4 w-4 mr-2" />
              Eliminar
            </Button>
          )}
          <p className="text-xs text-muted-foreground">
            JPG, PNG o WebP. Máx. 5MB
          </p>
        </div>
      </div>
      
      {/* Or URL input */}
      <div className="flex gap-2 items-center mt-2">
        <span className="text-xs text-muted-foreground">o URL:</span>
        <Input
          value={currentImage || ''}
          onChange={(e) => onImageChange(e.target.value)}
          placeholder="https://ejemplo.com/imagen.jpg"
          className="text-sm"
        />
      </div>
    </div>
  );
};

// Rich Text Editor Component with Image Support
const RichTextEditor = ({ content, onChange, placeholder, minHeight = '150px' }) => {
  const { token } = useAuth();
  const fileInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
    }
  }, [content, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL:', previousUrl);

    if (url === null) return;

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const addImage = useCallback(() => {
    const url = window.prompt('URL de la imagen:');
    if (url && editor) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;

    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona un archivo de imagen');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('La imagen no puede superar los 5MB');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result;
        
        const response = await fetch(`${API_URL}/api/upload-image`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            image: base64,
            filename: file.name,
            folder: 'content'
          })
        });

        if (response.ok) {
          const data = await response.json();
          editor.chain().focus().setImage({ src: `${API_URL}${data.url}` }).run();
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Error uploading image:', err);
    }
  };

  if (!editor) {
    return null;
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="bg-muted/50 border-b px-2 py-1.5 flex flex-wrap gap-0.5">
        {/* Headings */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={`h-8 w-8 p-0 ${editor.isActive('heading', { level: 1 }) ? 'bg-muted' : ''}`}
          title="Encabezado 1"
        >
          <Heading1 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`h-8 w-8 p-0 ${editor.isActive('heading', { level: 2 }) ? 'bg-muted' : ''}`}
          title="Encabezado 2"
        >
          <Heading2 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={`h-8 w-8 p-0 ${editor.isActive('heading', { level: 3 }) ? 'bg-muted' : ''}`}
          title="Encabezado 3"
        >
          <Heading3 className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-border mx-1 self-center" />

        {/* Text formatting */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`h-8 w-8 p-0 ${editor.isActive('bold') ? 'bg-muted' : ''}`}
          title="Negrita"
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`h-8 w-8 p-0 ${editor.isActive('italic') ? 'bg-muted' : ''}`}
          title="Cursiva"
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`h-8 w-8 p-0 ${editor.isActive('underline') ? 'bg-muted' : ''}`}
          title="Subrayado"
        >
          <UnderlineIcon className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={`h-8 w-8 p-0 ${editor.isActive('strike') ? 'bg-muted' : ''}`}
          title="Tachado"
        >
          <Strikethrough className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-border mx-1 self-center" />

        {/* Lists */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`h-8 w-8 p-0 ${editor.isActive('bulletList') ? 'bg-muted' : ''}`}
          title="Lista con viñetas"
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`h-8 w-8 p-0 ${editor.isActive('orderedList') ? 'bg-muted' : ''}`}
          title="Lista numerada"
        >
          <ListOrdered className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-border mx-1 self-center" />

        {/* Alignment */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={`h-8 w-8 p-0 ${editor.isActive({ textAlign: 'left' }) ? 'bg-muted' : ''}`}
          title="Alinear izquierda"
        >
          <AlignLeft className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={`h-8 w-8 p-0 ${editor.isActive({ textAlign: 'center' }) ? 'bg-muted' : ''}`}
          title="Centrar"
        >
          <AlignCenter className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={`h-8 w-8 p-0 ${editor.isActive({ textAlign: 'right' }) ? 'bg-muted' : ''}`}
          title="Alinear derecha"
        >
          <AlignRight className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-border mx-1 self-center" />

        {/* Link */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={setLink}
          className={`h-8 w-8 p-0 ${editor.isActive('link') ? 'bg-muted' : ''}`}
          title="Insertar enlace"
        >
          <LinkIcon className="h-4 w-4" />
        </Button>

        {/* Image */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleImageUpload}
          accept="image/*"
          className="hidden"
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          className="h-8 w-8 p-0"
          title="Subir imagen"
        >
          <Upload className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={addImage}
          className="h-8 w-8 p-0"
          title="Insertar imagen por URL"
        >
          <ImageIcon className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-border mx-1 self-center" />

        {/* Undo/Redo */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          className="h-8 w-8 p-0"
          title="Deshacer"
        >
          <Undo className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          className="h-8 w-8 p-0"
          title="Rehacer"
        >
          <Redo className="h-4 w-4" />
        </Button>
      </div>

      {/* Editor */}
      <EditorContent 
        editor={editor} 
        className="prose prose-sm max-w-none p-3"
        style={{ minHeight }}
      />

      <style>{`
        .ProseMirror {
          outline: none;
          min-height: ${minHeight};
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #adb5bd;
          pointer-events: none;
          height: 0;
        }
        .ProseMirror h1 { font-size: 1.5rem; font-weight: bold; margin: 0.5rem 0; }
        .ProseMirror h2 { font-size: 1.25rem; font-weight: bold; margin: 0.5rem 0; }
        .ProseMirror h3 { font-size: 1.1rem; font-weight: bold; margin: 0.5rem 0; }
        .ProseMirror ul, .ProseMirror ol { padding-left: 1.5rem; }
        .ProseMirror a { color: #dc2626; text-decoration: underline; }
        .ProseMirror img { max-width: 100%; height: auto; border-radius: 0.5rem; margin: 0.5rem 0; }
      `}</style>
    </div>
  );
};

// Simple Text Editor (for short descriptions)
const SimpleTextEditor = ({ content, onChange, placeholder }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
      }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
    }
  }, [content, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL:', previousUrl);

    if (url === null) return;

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Simple Toolbar */}
      <div className="bg-muted/50 border-b px-2 py-1 flex gap-0.5">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`h-7 w-7 p-0 ${editor.isActive('bold') ? 'bg-muted' : ''}`}
          title="Negrita"
        >
          <Bold className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`h-7 w-7 p-0 ${editor.isActive('italic') ? 'bg-muted' : ''}`}
          title="Cursiva"
        >
          <Italic className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`h-7 w-7 p-0 ${editor.isActive('underline') ? 'bg-muted' : ''}`}
          title="Subrayado"
        >
          <UnderlineIcon className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={setLink}
          className={`h-7 w-7 p-0 ${editor.isActive('link') ? 'bg-muted' : ''}`}
          title="Insertar enlace"
        >
          <LinkIcon className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Editor */}
      <EditorContent 
        editor={editor} 
        className="prose prose-sm max-w-none p-2"
      />

      <style>{`
        .ProseMirror {
          outline: none;
          min-height: 60px;
        }
        .ProseMirror a { color: #dc2626; text-decoration: underline; }
      `}</style>
    </div>
  );
};

export const SiteContentTab = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  // Homepage/Frontpage content
  const [homepage, setHomepage] = useState({
    hero: {
      title: 'Marketplace de Enlaces Premium',
      subtitle: 'Encuentra los mejores sitios web para tus campañas de link building. Filtros avanzados, métricas detalladas y la mejor selección de dominios.',
      ctaText: 'Explorar Catálogo',
      ctaSecondaryText: 'Ver Precios',
      heroImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop&q=80'
    },
    stats: {
      stat1Value: '+30.000',
      stat1Label: 'Sitios Web',
      stat2Value: '+16',
      stat2Label: 'Países Disponibles',
      stat3Value: '+10',
      stat3Label: 'Filtros Profesionales'
    },
    features: {
      title: 'Por qué elegir Purolink',
      subtitle: 'La plataforma más completa para link building profesional'
    },
    floatingCards: {
      card1Label: 'Average',
      card1Value: '72+',
      card1Title: 'DR',
      card2Label: 'From',
      card2Value: '€45',
      card2Title: '€'
    }
  });

  // Footer content
  const [footer, setFooter] = useState({
    contact: {
      email: 'soporte@purolink.com',
      phone: '+34 910 123 456',
      address: 'Madrid, España',
      website: 'www.purolink.com',
      schedule: 'Lun - Vie: 9:00 - 18:00'
    },
    social: {
      linkedin: '',
      twitter: '',
      instagram: '',
      facebook: ''
    }
  });

  // Legal pages content
  const [privacy, setPrivacy] = useState({ title: 'POLÍTICA DE PRIVACIDAD', content: '' });
  const [terms, setTerms] = useState({ title: 'TÉRMINOS Y CONDICIONES', content: '' });
  const [cookies, setCookies] = useState({ title: 'POLÍTICA DE COOKIES', content: '' });
  const [legal, setLegal] = useState({ title: 'AVISO LEGAL', content: '' });

  useEffect(() => {
    loadAllContent();
  }, []);

  const loadAllContent = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/site-content`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.homepage) setHomepage(prev => ({ 
          ...prev, 
          ...data.homepage,
          hero: { ...prev.hero, ...data.homepage.hero },
          stats: { ...prev.stats, ...data.homepage.stats },
          features: { ...prev.features, ...data.homepage.features },
          floatingCards: { ...prev.floatingCards, ...data.homepage.floatingCards }
        }));
        if (data.footer) setFooter(prev => ({ ...prev, ...data.footer }));
        if (data.privacy) setPrivacy(prev => ({ ...prev, ...data.privacy }));
        if (data.terms) setTerms(prev => ({ ...prev, ...data.terms }));
        if (data.cookies) setCookies(prev => ({ ...prev, ...data.cookies }));
        if (data.legal) setLegal(prev => ({ ...prev, ...data.legal }));
      }
    } catch (err) {
      console.error('Error loading content:', err);
    }
    setLoading(false);
  };

  const saveContent = async (contentType, content) => {
    setSaving(true);
    setSuccess('');
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/site-content/${contentType}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content_type: contentType, content })
      });
      
      if (response.ok) {
        setSuccess(`Contenido guardado correctamente`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        let detail = `Error ${response.status} al guardar`;
        try {
          const data = await response.json();
          detail = data.detail || detail;
        } catch (_) { /* response wasn't JSON */ }
        setError(detail);
        console.error('[SiteContent save failed]', { contentType, status: response.status, detail });
      }
    } catch (err) {
      setError('Error de conexión: ' + (err?.message || 'desconocido'));
      console.error('[SiteContent save error]', err);
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Contenido del Sitio</h2>
        <p className="text-muted-foreground">Edita el frontpage, footer, páginas legales y contenido de contacto</p>
      </div>

      {success && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700">{success}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="homepage" className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="homepage" className="text-xs sm:text-sm">
            <Home className="h-4 w-4 mr-1 hidden sm:inline" />
            Frontpage
          </TabsTrigger>
          <TabsTrigger value="footer" className="text-xs sm:text-sm">
            <Globe className="h-4 w-4 mr-1 hidden sm:inline" />
            Footer
          </TabsTrigger>
          <TabsTrigger value="privacy" className="text-xs sm:text-sm">
            <Shield className="h-4 w-4 mr-1 hidden sm:inline" />
            Privacidad
          </TabsTrigger>
          <TabsTrigger value="terms" className="text-xs sm:text-sm">
            <FileText className="h-4 w-4 mr-1 hidden sm:inline" />
            Términos
          </TabsTrigger>
          <TabsTrigger value="cookies" className="text-xs sm:text-sm">
            <Cookie className="h-4 w-4 mr-1 hidden sm:inline" />
            Cookies
          </TabsTrigger>
          <TabsTrigger value="legal" className="text-xs sm:text-sm">
            <Scale className="h-4 w-4 mr-1 hidden sm:inline" />
            Aviso Legal
          </TabsTrigger>
        </TabsList>

        {/* Homepage/Frontpage Tab */}
        <TabsContent value="homepage" className="mt-6">
          <div className="space-y-6">
            {/* Hero Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Type className="h-5 w-5 text-primary" />
                  Sección Hero
                </CardTitle>
                <CardDescription>Textos principales que aparecen en la parte superior de la página de inicio</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="hero-title">Título Principal</Label>
                  <Input
                    id="hero-title"
                    value={homepage.hero?.title || ''}
                    onChange={(e) => setHomepage(prev => ({
                      ...prev,
                      hero: { ...prev.hero, title: e.target.value }
                    }))}
                    placeholder="Marketplace de Enlaces Premium"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Subtítulo / Descripción</Label>
                  <SimpleTextEditor
                    content={homepage.hero?.subtitle || ''}
                    onChange={(value) => setHomepage(prev => ({
                      ...prev,
                      hero: { ...prev.hero, subtitle: value }
                    }))}
                    placeholder="Encuentra los mejores sitios web..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="hero-cta">Texto Botón Principal</Label>
                    <Input
                      id="hero-cta"
                      value={homepage.hero?.ctaText || ''}
                      onChange={(e) => setHomepage(prev => ({
                        ...prev,
                        hero: { ...prev.hero, ctaText: e.target.value }
                      }))}
                      placeholder="Explorar Catálogo"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="hero-cta-secondary">Texto Botón Secundario</Label>
                    <Input
                      id="hero-cta-secondary"
                      value={homepage.hero?.ctaSecondaryText || ''}
                      onChange={(e) => setHomepage(prev => ({
                        ...prev,
                        hero: { ...prev.hero, ctaSecondaryText: e.target.value }
                      }))}
                      placeholder="Ver Precios"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Hero Image */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ImageIcon className="h-5 w-5 text-primary" />
                  Imagen Principal del Hero
                </CardTitle>
                <CardDescription>La imagen grande que aparece en el lado derecho del hero</CardDescription>
              </CardHeader>
              <CardContent>
                <ImageUploader
                  currentImage={homepage.hero?.heroImage || ''}
                  onImageChange={(url) => setHomepage(prev => ({
                    ...prev,
                    hero: { ...prev.hero, heroImage: url }
                  }))}
                  label="Imagen del Hero"
                  folder="hero"
                />
              </CardContent>
            </Card>

            {/* Floating Cards */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-primary" />
                  Tarjetas Flotantes
                </CardTitle>
                <CardDescription>Las pequeñas tarjetas que flotan sobre la imagen del hero (DR 72+, €45)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Card 1 */}
                  <div className="space-y-3 p-4 bg-green-50 rounded-lg border border-green-200">
                    <h4 className="font-medium text-sm text-green-700">Tarjeta 1 (Verde - DR)</h4>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Título</Label>
                        <Input
                          value={homepage.floatingCards?.card1Title || 'DR'}
                          onChange={(e) => setHomepage(prev => ({
                            ...prev,
                            floatingCards: { ...prev.floatingCards, card1Title: e.target.value }
                          }))}
                          placeholder="DR"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Etiqueta</Label>
                        <Input
                          value={homepage.floatingCards?.card1Label || 'Average'}
                          onChange={(e) => setHomepage(prev => ({
                            ...prev,
                            floatingCards: { ...prev.floatingCards, card1Label: e.target.value }
                          }))}
                          placeholder="Average"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Valor</Label>
                        <Input
                          value={homepage.floatingCards?.card1Value || '72+'}
                          onChange={(e) => setHomepage(prev => ({
                            ...prev,
                            floatingCards: { ...prev.floatingCards, card1Value: e.target.value }
                          }))}
                          placeholder="72+"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Card 2 */}
                  <div className="space-y-3 p-4 bg-red-50 rounded-lg border border-red-200">
                    <h4 className="font-medium text-sm text-red-700">Tarjeta 2 (Roja - Precio)</h4>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Título</Label>
                        <Input
                          value={homepage.floatingCards?.card2Title || '€'}
                          onChange={(e) => setHomepage(prev => ({
                            ...prev,
                            floatingCards: { ...prev.floatingCards, card2Title: e.target.value }
                          }))}
                          placeholder="€"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Etiqueta</Label>
                        <Input
                          value={homepage.floatingCards?.card2Label || 'From'}
                          onChange={(e) => setHomepage(prev => ({
                            ...prev,
                            floatingCards: { ...prev.floatingCards, card2Label: e.target.value }
                          }))}
                          placeholder="From"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Valor</Label>
                        <Input
                          value={homepage.floatingCards?.card2Value || '€45'}
                          onChange={(e) => setHomepage(prev => ({
                            ...prev,
                            floatingCards: { ...prev.floatingCards, card2Value: e.target.value }
                          }))}
                          placeholder="€45"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stats Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-primary" />
                  Estadísticas
                </CardTitle>
                <CardDescription>Los números destacados que aparecen en el hero</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[1, 2, 3].map((num) => (
                    <div key={num} className="space-y-3 p-4 bg-muted/50 rounded-lg">
                      <h4 className="font-medium text-sm text-muted-foreground">Estadística {num}</h4>
                      <div className="space-y-2">
                        <Label>Valor</Label>
                        <Input
                          value={homepage.stats?.[`stat${num}Value`] || ''}
                          onChange={(e) => setHomepage(prev => ({
                            ...prev,
                            stats: { ...prev.stats, [`stat${num}Value`]: e.target.value }
                          }))}
                          placeholder={num === 1 ? '+30.000' : num === 2 ? '+16' : '+10'}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Etiqueta</Label>
                        <Input
                          value={homepage.stats?.[`stat${num}Label`] || ''}
                          onChange={(e) => setHomepage(prev => ({
                            ...prev,
                            stats: { ...prev.stats, [`stat${num}Label`]: e.target.value }
                          }))}
                          placeholder={num === 1 ? 'Sitios Web' : num === 2 ? 'Países Disponibles' : 'Filtros Profesionales'}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Features Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MousePointer className="h-5 w-5 text-primary" />
                  Sección de Características
                </CardTitle>
                <CardDescription>Textos de la sección "Por qué elegir Purolink"</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="features-title">Título de la Sección</Label>
                  <Input
                    id="features-title"
                    value={homepage.features?.title || ''}
                    onChange={(e) => setHomepage(prev => ({
                      ...prev,
                      features: { ...prev.features, title: e.target.value }
                    }))}
                    placeholder="Por qué elegir Purolink"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Subtítulo</Label>
                  <SimpleTextEditor
                    content={homepage.features?.subtitle || ''}
                    onChange={(value) => setHomepage(prev => ({
                      ...prev,
                      features: { ...prev.features, subtitle: value }
                    }))}
                    placeholder="La plataforma más completa..."
                  />
                </div>
              </CardContent>
            </Card>

            <Button onClick={() => saveContent('homepage', homepage)} disabled={saving} size="lg">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Guardar Frontpage
            </Button>
          </div>
        </TabsContent>

        {/* Footer Tab */}
        <TabsContent value="footer" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  Información de Contacto
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { id: 'email', icon: Mail, label: 'Email', placeholder: 'soporte@purolink.com' },
                  { id: 'phone', icon: Phone, label: 'Teléfono', placeholder: '+34 910 123 456' },
                  { id: 'address', icon: MapPin, label: 'Dirección', placeholder: 'Madrid, España' },
                  { id: 'website', icon: Globe, label: 'Website', placeholder: 'www.purolink.com' },
                ].map(({ id, icon: Icon, label, placeholder }) => (
                  <div key={id} className="space-y-2">
                    <Label className="flex items-center gap-2"><Icon className="h-4 w-4" /> {label}</Label>
                    <Input
                      value={footer.contact[id]}
                      onChange={(e) => setFooter(prev => ({
                        ...prev,
                        contact: { ...prev.contact, [id]: e.target.value }
                      }))}
                      placeholder={placeholder}
                    />
                  </div>
                ))}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Clock className="h-4 w-4" /> Horario</Label>
                  <Textarea
                    value={footer.contact.schedule}
                    onChange={(e) => setFooter(prev => ({
                      ...prev,
                      contact: { ...prev.contact, schedule: e.target.value }
                    }))}
                    placeholder="Lunes a Jueves: 9:00 - 18:00&#10;Viernes: 9:00 - 15:00"
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Globe className="h-5 w-5 text-primary" />
                  Redes Sociales
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {['linkedin', 'twitter', 'instagram', 'facebook'].map((social) => (
                  <div key={social} className="space-y-2">
                    <Label className="capitalize">{social === 'twitter' ? 'Twitter / X' : social}</Label>
                    <Input
                      value={footer.social?.[social] || ''}
                      onChange={(e) => setFooter(prev => ({
                        ...prev,
                        social: { ...prev.social, [social]: e.target.value }
                      }))}
                      placeholder={`https://${social}.com/purolink`}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
          <div className="mt-6">
            <Button onClick={() => saveContent('footer', footer)} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Guardar Footer
            </Button>
          </div>
        </TabsContent>

        {/* Legal Pages Tabs */}
        {[
          { key: 'privacy', icon: Shield, title: 'Política de Privacidad', state: privacy, setState: setPrivacy },
          { key: 'terms', icon: FileText, title: 'Términos y Condiciones', state: terms, setState: setTerms },
          { key: 'cookies', icon: Cookie, title: 'Política de Cookies', state: cookies, setState: setCookies },
          { key: 'legal', icon: Scale, title: 'Aviso Legal', state: legal, setState: setLegal },
        ].map(({ key, icon: Icon, title, state, setState }) => (
          <TabsContent key={key} value={key} className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Icon className="h-5 w-5 text-primary" />
                  {title}
                </CardTitle>
                <CardDescription>Usa el editor visual para dar formato al contenido. Puedes añadir imágenes.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Título</Label>
                  <Input
                    value={state.title}
                    onChange={(e) => setState(prev => ({ ...prev, title: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contenido</Label>
                  <RichTextEditor
                    content={state.content}
                    onChange={(value) => setState(prev => ({ ...prev, content: value }))}
                    minHeight="400px"
                  />
                </div>
                <Button onClick={() => saveContent(key, state)} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Guardar {title.split(' ')[0]}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default SiteContentTab;
